import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Job } from '@libs/graphql-types';
import Button from '@libs/ui/components/button';

import {
  JobHighlightsAccordion,
  JobLocationAccordion,
  RatesAccordion,
  JobInfoAccordion,
  VmsAccordion,
  InformationAccordion,
  SystemInfoAccordion,
  BuyerInfoAccordion,
  RequisitionInfoAccordion,
  AdditionalInfoAccordion,
  QuestionsQualificationsAccordion,
} from '.';
import { USJobStatus } from '../../job-header';

const useStyles = makeStyles((theme: any) => ({
  container: {},
  headerContainer: {
    padding: theme.spacing(6, 4),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  chipContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  baseChip: {
    color: theme.palette.common.white,
    fontSize: '0.75rem',
    position: 'relative',
    fontWeight: 500,
    height: 28,
    borderRadius: 4,
    padding: theme.spacing(0, 1.5),
    textTransform: 'none',
    right: -22,
    '& .MuiChip-label': {
      padding: theme.spacing(0, 0.5),
    },
  },
  activeChip: {
    backgroundColor: '#00c853', // green
  },
  archivedChip: {
    backgroundColor: '#9e9e9e', // gray
  },
  draftChip: {
    backgroundColor: '#ff9800', // orange
  },
  accordionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  showLessMoreContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(4, 4, 2, 4),
  },
  showLessMoreButton: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    padding: theme.spacing(0),
  },
}));

interface CampaignDetailsProps {
  jobData: Job;
}

export const CampaignDetails: React.FC<CampaignDetailsProps> = ({
  jobData,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('job');

  // State for tracking expanded/collapsed state of each accordion
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    jobHighlights: true,
    jobLocation: true,
    rates: true,
    jobInfo: true,
    shiftInfo: true,
    vms: true,
    information: true,
    systemInfo: true,
    buyerInfo: true,
    rateInfo: true,
    requisitionInfo: true,
    additionalInfo: true,
    questionsQualifications: true,
  });

  // Check if all sections are open or closed
  const allExpanded = Object.values(expanded).every((value) => value);
  const allCollapsed = Object.values(expanded).every((value) => !value);

  // Toggle specific accordion
  const handleAccordionChange = useCallback(
    (section: string, isExpanded: boolean) => {
      setExpanded((prev) => ({
        ...prev,
        [section]: isExpanded,
      }));
    },
    [],
  );

  // Toggle all accordions
  const toggleAllAccordions = useCallback(() => {
    // If any section is open, close all; otherwise, open all
    const newValue = !!allCollapsed;

    setExpanded({
      jobHighlights: newValue,
      jobLocation: newValue,
      rates: newValue,
      jobInfo: newValue,
      shiftInfo: newValue,
      vms: newValue,
      information: newValue,
      systemInfo: newValue,
      buyerInfo: newValue,
      rateInfo: newValue,
      requisitionInfo: newValue,
      additionalInfo: newValue,
      questionsQualifications: newValue,
    });
  }, [allCollapsed]);

  return (
    <Grid container className={classes.container}>
      <Grid container item className={classes.headerContainer}>
        <Grid item xs={6}>
          <Typography className={classes.title}>{t('v2.about')}</Typography>
        </Grid>
        <Grid item xs={6} className={classes.chipContainer}>
          <USJobStatus job={jobData} className={classes.baseChip} />
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.accordionContainer}>
        <JobHighlightsAccordion
          jobData={jobData}
          expanded={expanded.jobHighlights}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('jobHighlights', isExpanded)
          }
        />
        <JobLocationAccordion
          jobData={jobData}
          expanded={expanded.jobLocation}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('jobLocation', isExpanded)
          }
        />
        <RatesAccordion
          jobData={jobData}
          expanded={expanded.rates}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('rates', isExpanded)
          }
        />
        <JobInfoAccordion
          jobData={jobData}
          expanded={expanded.jobInfo}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('jobInfo', isExpanded)
          }
        />
        {/* <ShiftInfoAccordion
          jobData={jobData}
          expanded={expanded.shiftInfo}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('shiftInfo', isExpanded)
          }
        /> */}
        <VmsAccordion
          jobData={jobData}
          expanded={expanded.vms}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('vms', isExpanded)
          }
        />
        <InformationAccordion
          jobData={jobData}
          expanded={expanded.information}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('information', isExpanded)
          }
        />
        <SystemInfoAccordion
          jobData={jobData}
          expanded={expanded.systemInfo}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('systemInfo', isExpanded)
          }
        />
        <BuyerInfoAccordion
          jobData={jobData}
          expanded={expanded.buyerInfo}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('buyerInfo', isExpanded)
          }
        />
        <RequisitionInfoAccordion
          jobData={jobData}
          expanded={expanded.requisitionInfo}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('requisitionInfo', isExpanded)
          }
        />
        <QuestionsQualificationsAccordion
          jobData={jobData}
          expanded={expanded.questionsQualifications}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('questionsQualifications', isExpanded)
          }
        />
        <AdditionalInfoAccordion
          jobData={jobData}
          expanded={expanded.additionalInfo}
          onChange={(isExpanded: boolean) =>
            handleAccordionChange('additionalInfo', isExpanded)
          }
        />

        <div className={classes.showLessMoreContainer}>
          <Button
            onClick={toggleAllAccordions}
            color="info"
            className={classes.showLessMoreButton}
            endIcon={allExpanded ? <KeyboardArrowUpIcon /> : undefined}
          >
            {allCollapsed ? t('v2.showMore') : t('v2.showLess')}
          </Button>
        </div>
      </Grid>
    </Grid>
  );
};
